import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  DialogContentText,
  Fab,
  Grid,
  Link,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import JobProgressDialog from "./JobProgressDialog";
import ScheduleJobDialog from "../ShopSchedule/ShopCalendar/ScheduleJobDialog";
import useSchedule from "../../../hooks/useSchedule";
import { SnackAlertContext } from "../../../context/SnackAlertContext";
import { Add } from "@mui/icons-material";
import useJobs from "../../../hooks/useJobs";
import { useDebounce } from "use-debounce";
import { useInView } from "react-intersection-observer";
import InfiniteScrollerTable from "../../../components/InfiniteScroller/InfiniteScrollerTable";
import ListFooter from "../../../components/InfiniteScroller/ListFooter";
import TextFieldDebounced from "../../../components/TextFieldDebounced";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // ES 2015
import timezone from "dayjs/plugin/timezone"; // ES 2015
import isoWeek from "dayjs/plugin/isoWeek"; // ES 2015
import { currencyFormatNoDecimals } from "../../../components/NewDetail/calculations/utils";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
const DEFAULT_TIMEZONE = "America/Phoenix";
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

const DEFAULT_SCHEDULE_JOB_DIALOG_DATA = {
  open: false,
  date: null,
  jobId: null,
};

const Jobs = ({ search, status }) => {
  const { authToken, ezorder } = useContext(AuthContext);
  const { openSnackMessage } = useContext(SnackAlertContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const { scheduleEvents, updateEvent } = useSchedule();
  const [selectedJob, setSelectedJob] = useState(null);
  const [scheduleJobDialogData, setScheduleJobDialogData] = useState(
    DEFAULT_SCHEDULE_JOB_DIALOG_DATA,
  );

  const [jobs, onRefresh, refreshing, onLoadMore, loadingMore, total, hasMore] =
    useJobs(authToken, 50, null, search, status);

  const [ref, inView, entry] = useInView({
    /* Optional options */
    threshold: 0.5, //Number between 0 and 1 indicating the percentage that should be visible before triggering. Can also be an array of numbers, to create multiple trigger points.
  });

  const [inViewDebounce] = useDebounce(inView, 1000);

  const updateSearch = (searchUpdate, statusUpdate) => {
    let query = [];
    if (searchUpdate || statusUpdate) {
      if (searchUpdate) {
        query.push(`search=${searchUpdate}`);
      }
      if (statusUpdate) {
        query.push(`status=${statusUpdate}`);
      }
    }
    if (query.length > 0) {
      navigate(`/jobs?${query.join("&")}`); // search=hello&status=DRAFT
    } else {
      navigate(`/jobs`);
    }
  };

  useEffect(() => {
    if (jobs.length > 0 && inView) {
      console.log("Footer Element In View. Load More...");
      onLoadMore();
    }
  }, [inViewDebounce]);

  const handleScheduleEvent = async (eventDetails) => {
    try {
      const response = await scheduleEvents([eventDetails]);
      console.log("eventDetails", eventDetails);
      if (response.status == 200 || response.status == 201) {
        //TODO: navigate
        let start = dayjs(eventDetails.startDate)
          .tz("America/Phoenix")
          .format("YYYY-MM-DD");
        navigate(`/shop-schedule?date=${start}&view=day`);
      } else {
        openSnackMessage("error", response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
    }
  };

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}
    >
      <Typography variant="h4" component="div">
        Jobs
      </Typography>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
          marginTop: "16px",
          marginBottom: "16px",
        }}
      >
        <Box sx={{ marginLeft: "8px", flex: 1 }}>
          <TextFieldDebounced
            fullWidth
            variant="outlined"
            label="Search"
            value={search}
            onChange={(e) => {
              updateSearch(e.target.value, status);
            }}
          />
        </Box>
        <TextField
          sx={{ width: "200px", marginLeft: "8px" }}
          select
          label="Status"
          value={status}
          onChange={(e) => {
            updateSearch(search, e.target.value);
          }}
        >
          <MenuItem value={"OPEN"}>Open</MenuItem>
          <MenuItem value={"CLOSED"}>Completed</MenuItem>
          <MenuItem value={"EXPIRED"}>Expired</MenuItem>
          <MenuItem value={"ALL"}>All</MenuItem>
        </TextField>
      </Box>
      <InfiniteScrollerTable
        TableContainerComponent={Paper}
        TableStyle={{ minWidth: "600px" }}
        TableHead={() => (
          <TableHead>
            <TableRow>
              <TableCell align="left">Date Created</TableCell>
              <TableCell align="left">Date Approved</TableCell>
              <TableCell align="left">Date Completed</TableCell>
              <TableCell align="left">Job No.</TableCell>
              <TableCell align="left">Project</TableCell>
              <TableCell align="left">Customer</TableCell>
              {/* <TableCell align="left">Payment Status</TableCell> */}
              {/* <TableCell align="left">Scheduled Tasks</TableCell> */}
              <TableCell align="left">$</TableCell>
            </TableRow>
          </TableHead>
        )}
        items={jobs}
        renderRow={(item, index) => {
          let scheduledSubPhases = 0;
          let totalSubPhases = 0;
          for (let phase of item.phases) {
            for (let subPhase of phase.subPhases) {
              totalSubPhases++;
              if (
                subPhase.calendarEvents &&
                subPhase.calendarEvents.length > 0
              ) {
                scheduledSubPhases++;
              }
            }
          }

          return (
            <TableRow
              key={item.id}
              hover
              sx={{
                "&:last-child td, &:last-child th": { border: 0 },
                cursor: "pointer",
                background:
                  item.company.creditType === "Pay in Advance" ||
                  item.company.creditType === "Cash On Delivery"
                    ? "#ffecec"
                    : "inherit",
              }}
              onClick={() => {
                // setSelectedJob(item);
                navigate(`/orders/${item.id}`);
              }}
            >
              <TableCell align="left">
                {dayjs(item.createdAt).tz("America/Phoenix").format("MM/DD/YY")}
              </TableCell>
              <TableCell align="left">
                {dayjs(item.estimateApprovalDate)
                  .tz("America/Phoenix")
                  .format("MM/DD/YY")}
              </TableCell>
              <TableCell align="left">
                {item.fulfilledOn
                  ? dayjs(item.fulfilledOn)
                      .tz("America/Phoenix")
                      .format("MM/DD/YY")
                  : ""}
              </TableCell>
              <TableCell align="left">{item.id}</TableCell>
              <TableCell align="left">{item.project.name}</TableCell>
              <TableCell align="left">
                {item.company && item.company.name
                  ? item.company.name
                  : "GUEST"}
              </TableCell>
              {/* <TableCell align="left">
                <Link
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    navigate(`/invoices/${item.id}`);
                  }}
                >
                  {item.invoiceId
                    ? item.isPaid
                      ? "Paid"
                      : "Unpaid"
                    : "Invoice Pending"}
                </Link>
              </TableCell> */}

              {/* <TableCell align="left">
                <Button
                  color={
                    scheduledSubPhases == totalSubPhases ? "success" : "warning"
                  }
                >
                  {scheduledSubPhases} / {totalSubPhases}
                </Button>
              </TableCell> */}
              <TableCell align="left">
                {currencyFormatNoDecimals(
                  Math.round(Number(item.outTheDoorCost ?? 0)),
                )}
              </TableCell>
            </TableRow>
          );
        }}
        onRefresh={onRefresh}
        onEndReached={onLoadMore}
        loading={refreshing || loadingMore}
        ListFooterComponent={() => {
          return <ListFooter hasMore={hasMore} onClick={onLoadMore} />;
        }}
      />

      <div
        style={{
          position: "fixed",
          bottom: "20px",
          right: "50px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <Fab
          onClick={() => {
            navigate("/jobs/create");
          }}
          variant="extended"
          color="success"
        >
          <Add /> Job
        </Fab>
      </div>
      <JobProgressDialog
        open={selectedJob != null}
        jobDetails={selectedJob}
        handleSchedule={(jobDetails) => {
          setScheduleJobDialogData({
            open: true,
            calendarEvent: {
              date: dayjs().tz(DEFAULT_TIMEZONE).startOf("day"),
              startDate: dayjs()
                .tz(DEFAULT_TIMEZONE)
                .startOf("day")
                .add(7, "hours"),
              endDate: dayjs()
                .tz(DEFAULT_TIMEZONE)
                .startOf("day")
                .add(8, "hours"),
              eventEntity: "IND",
              order: jobDetails,
              subPhases: [],
            },
          });
          setSelectedJob(null);
        }}
        handleClose={() => {
          setSelectedJob(null);
        }}
      />
      <ScheduleJobDialog
        dialogData={scheduleJobDialogData}
        handleClose={() => {
          setScheduleJobDialogData(DEFAULT_SCHEDULE_JOB_DIALOG_DATA);
        }}
        handleSubmit={(eventDetails) => {
          handleScheduleEvent(eventDetails);
        }}
      />
    </Box>
  );
};
export default Jobs;
